import React, { useState } from 'react'
import type { FC } from 'react'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import { useGetOutreachRequestsQuery } from '@homeviews/store'
import { Pagination, Table } from '@homeviews/shared-ui'
import { Typography } from '@homeviews/outreach'

const useStyles = makeStyles(() =>
  createStyles({
    customH1: {
      '.MuiTypography-h1&': {
        display: 'inline-block',
        marginBottom: '0',
      },
    },
    subtitleWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    customSubtitle2: {
      '.MuiTypography-subtitle2&': {
        display: 'inline-block',
        marginBottom: '20px',
      },
    },
    tableWrapper: {
      margin: '10px 0',
      '& .MuiTableCell-head': {
        paddingLeft: '25px',
        letterSpacing: '-0.2px',
      },
      '& .MuiTableCell-body': {
        paddingLeft: '25px',
        fontSize: '14px',
        letterSpacing: '-0.26px',
      },
    },
  }),
)

// Convert 43.46 to '43%'
const numToStrPercentage = (num: number) => num.toFixed(0) + '%'

export const RequestsTable: FC = () => {
  const classes = useStyles()

  const [currentPage, setCurrentPage] = useState(1)
  const handlePageChange = (newPage: number) => setCurrentPage(newPage)

  const [perPage, setPerPage] = useState(10)
  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset to page 1 to prevent some issues
  }

  const { data } = useGetOutreachRequestsQuery(
    {
      page: currentPage,
      perPage: perPage,
    },
    { refetchOnMountOrArgChange: true },
  )

  const rows = () => {
    return data.data.map((request) => {
      const dateSent = new Date(Date.parse(request.createdAt)) // API gives ISO 8601
      return [
        'ignored',
        request.title,
        request.development.name,
        request.type,
        dateSent.toLocaleDateString(), // Shows as DD/MM/YY for UK
        numToStrPercentage(request.openRate),
        numToStrPercentage(request.ctr),
        numToStrPercentage(request.completionRate),
      ]
    })
  }

  if (!data || data.data.length === 0) return null
  else
    return (
      <>
        <Typography variant="h1" className={classes.customH1}>
          Sent Emails
        </Typography>
        <div className={classes.subtitleWrapper}>
          <Typography variant="subtitle2" className={classes.customSubtitle2}>
            {data.data.length} emails shown of {data.meta.total}
          </Typography>
          <Pagination
            currentPage={currentPage}
            lastPage={data.meta.last_page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </div>
        <div className={classes.tableWrapper}>
          <Table
            headCells={[
              { id: 'fileName', label: 'File name', width: 200 },
              { id: 'developmentName', label: 'Development name', width: 190 },
              { id: 'type', label: 'Type', width: 150 },
              { id: 'dateSent', label: 'Date sent', width: 130 },
              { id: 'openRate', label: 'Open rate', width: 110 },
              { id: 'ctr', label: 'CTR', width: 80 },
              { id: 'completion', label: 'Completion', width: 110 },
            ]}
            rows={rows()}
          />
        </div>
        <Pagination
          currentPage={currentPage}
          lastPage={data.meta.last_page}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
        <Typography variant="body1">
          Please note, due to recent email privacy changes, open rates may not
          reflect how many of your recipients actually opened your marketing
          email.
        </Typography>
      </>
    )
}
